import { Form } from 'antd';
import { FC, memo, useCallback, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router';

import { AppMessage } from '@/common/components';
import { AppRoutes, FormFieldsErrors } from '@/common/interfaces';
import { FallbackedLayout } from '@/common/layouts';
import { extractFormErrors, messageOnError } from '@/common/utils';

import {
  DomainsApiProviders,
  SearchConsoleAccountsFormFirstStepFields,
  SearchConsoleAccountsFormFirstStepInputs,
  SearchConsoleAccountsQueryKeys,
  useGetByIdSearchConsoleAccountsQuery,
  useUpdateSearchConsoleAccountsMutation
} from '@/modules/DomainsApi';

import { FirstStepView } from './FirstStepView';
import { searchConsoleAccountsFormFirstStepValidationSchema } from './validations';

interface Props {
  searchConsoleAccountId: string;
}

export const FirstStepEditContainer: FC<Props> = memo(({ searchConsoleAccountId }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const appMessageApi = AppMessage.useControls();

  const [form] = Form.useForm<SearchConsoleAccountsFormFirstStepFields>();
  const [formErrors,
    setFormErrors] =
    useState<FormFieldsErrors<typeof SearchConsoleAccountsFormFirstStepInputs>>(null);

  const {
    data: searchConsoleAccountData,
    error: searchConsoleAccountError,
    isLoading: isSearchConsoleAccountFetching,
    isError: isSearchConsoleAccountError
  } = useGetByIdSearchConsoleAccountsQuery(searchConsoleAccountId)({
    enabled: !!searchConsoleAccountId
  });

  const {
    mutate: updateAccountMutation,
    isLoading: isUpdateAccountLoading
  } = useUpdateSearchConsoleAccountsMutation(searchConsoleAccountId)({
    onSuccess: async (data) => {
      appMessageApi.success('Search Console акаунт успішно оновлено!');

      queryClient.setQueryData([SearchConsoleAccountsQueryKeys.GetOneById, data.id], data);

      await queryClient.refetchQueries([SearchConsoleAccountsQueryKeys.GetAll], { exact: false });

      if (data.clientId && data.clientSecret) {
        navigate(AppRoutes.DomainsApiSearchConsoleAccountsById.replace(':searchConsoleAccountId', data.id));
      } else {
        navigate(AppRoutes.DomainsApiSearchConsoleAccountsCreateCreds.replace(':searchConsoleAccountId', data.id));
      }
    },
    onError: (error) => {
      setFormErrors(extractFormErrors<typeof SearchConsoleAccountsFormFirstStepInputs>(error));
      messageOnError(error, appMessageApi);
    }
  });

  const initialValues: SearchConsoleAccountsFormFirstStepFields = {
    email: searchConsoleAccountData?.email,
    password: searchConsoleAccountData?.password,
    provider: DomainsApiProviders.SearchConsole,
    backupEmailAddress: searchConsoleAccountData?.backupEmailAddress
  };

  const handleFinish = useCallback((values: SearchConsoleAccountsFormFirstStepFields): void => {
    setFormErrors(null);
    updateAccountMutation(values);
  }, []);

  const handleFinishFailed = useCallback((): void => {
    setFormErrors(null);
  }, []);

  const handleBack = useCallback((): void => {
    navigate(AppRoutes.DomainsApiSearchConsoleAccountsById.replace(':searchConsoleAccountId', searchConsoleAccountId));
  }, [searchConsoleAccountId]);

  return (
    <FallbackedLayout
      isLoading={ isSearchConsoleAccountFetching }
      isError={ isSearchConsoleAccountError }
      error={ searchConsoleAccountError }
    >
      {
        () => (
          <FirstStepView
            formName={ 'edit-search-console-account-form-first-step' }
            formType={ 'edit' }
            form={ form }
            initialValues={ initialValues }
            formErrors={ formErrors }
            isFormLoading={ isUpdateAccountLoading }
            validationSchema={ searchConsoleAccountsFormFirstStepValidationSchema }
            handleBack={ handleBack }
            handleFinish={ handleFinish }
            handleFinishFailed={ handleFinishFailed }
          />
        )
      }
    </FallbackedLayout>
  );
});
